"use client"

import { ChainContainerModal } from "@src/components/auth/ChainContainerModal"
import {
  type ResolvedLastWallet,
  useResolvedLastWallet,
} from "@src/components/auth/useResolvedLastWallet"
import Badge from "@src/components/Badge"
import type { ChainIcon } from "@src/components/DefuseSDK/constants/blockchains"
import ErrorMessage from "@src/components/ErrorMessage"
import { SplashScreenContent } from "@src/components/SplashScreen"
import WalletButton from "@src/components/WalletButton"
import {
  HelpScoutArticleKey,
  openHelpScoutArticle,
} from "@src/config/helpScoutArticles"
import {
  ALWAYS_AVAILABLE_EVM_CONNECTOR_IDS,
  type ChainId,
  connectorIdsForWallet,
  findWalletById,
} from "@src/config/walletRegistry"
import { ReferredLoginNote } from "@src/features/referral/components/ReferredLoginNote"
import { useWebAuthnUIStore } from "@src/features/webauthn/hooks/useWebAuthnUiStore"
import { ChainType, useConnectWallet } from "@src/hooks/useConnectWallet"
import { useWalletSnapshot } from "@src/hooks/useWalletSnapshot"
import { NearIcon, NearIntentsLogoIcon, TouchIdIcon } from "@src/icons"
import { useBootstrapState } from "@src/providers/AppBootstrapProvider"
import { useNearWallet } from "@src/providers/NearWalletProvider"
import { useStellarWallet } from "@src/providers/StellarWalletProvider"
import Link from "next/link"
import { useEffect, useMemo, useState } from "react"
import { useConnectors } from "wagmi"
import {
  shouldShowLoginRestoreSplash,
  useSignedOutRestoreTimeout,
} from "./loginSplash"

// Registry fallbacks — used to fill the EVM tile's stacked icon row when
// the user has fewer than three EIP-6963-announced wallets installed.
// Order is the priority for filling: MetaMask first, then Rabby, then
// Coinbase. The matching `id` strings line up with what wagmi reports
// for installed extensions (rdns) or SDK connectors, so we can dedupe
// the live + static lists by id.
const EVM_FALLBACK_WALLET_IDS = ["metamask", "rabby", "coinbase"] as const
const EVM_REGISTRY_ICONS = EVM_FALLBACK_WALLET_IDS.flatMap((walletId) => {
  const wallet = findWalletById(walletId)
  if (!wallet?.icon) return []
  const icon = wallet.icon

  return connectorIdsForWallet(wallet, "evm").map((id) => ({
    id,
    icon,
  }))
})
const EVM_REGISTRY_ICON_BY_CONNECTOR_ID = new Map(
  EVM_REGISTRY_ICONS.map(({ id, icon }) => [id, icon])
)

export default function LoginPage() {
  const { signIn, signInError } = useConnectWallet()
  const { restoreStatus, interactionStatus } = useWalletSnapshot()
  const bootstrapState = useBootstrapState()
  const webauthnUI = useWebAuthnUIStore()
  const recentWallet = useResolvedLastWallet()
  const wagmiConnectors = useConnectors()
  const { ensureInitialized: ensureNearInit } = useNearWallet()
  const { refreshAvailableWallets: refreshStellar } = useStellarWallet()

  // Pre-load NEAR and Stellar wallet manifests on page mount so the
  // chain containers render their wallet lists instantly when opened
  useEffect(() => {
    void ensureNearInit()
    void refreshStellar()
  }, [ensureNearInit, refreshStellar])

  // EVM tile's stacked-icon row: prefer the user's actually-installed
  // wallets (announced live via EIP-6963), then pad with the registry
  // MetaMask -> Rabby -> Coinbase fallbacks in priority order. Dedupes
  // by connector id (which equals the rdns for EIP-6963 wallets), so
  // a live MetaMask icon doesn't double up with the static one.
  const evmIcons = useMemo<ChainIcon[]>(() => {
    const installed = wagmiConnectors.filter(
      (c) =>
        c.id !== "injected" &&
        !ALWAYS_AVAILABLE_EVM_CONNECTOR_IDS.includes(c.id)
    )
    const seen = new Set<string>()
    const icons: ChainIcon[] = []
    for (const c of installed) {
      if (icons.length >= 3) break
      if (seen.has(c.id)) continue
      const registryIcon = EVM_REGISTRY_ICON_BY_CONNECTOR_ID.get(c.id)
      const url = c.icon?.trim()
      const icon = registryIcon ?? (url ? { dark: url, light: url } : undefined)
      if (!icon) continue
      seen.add(c.id)
      icons.push(icon)
    }
    for (const fallback of EVM_REGISTRY_ICONS) {
      if (icons.length >= 3) break
      if (seen.has(fallback.id)) continue
      seen.add(fallback.id)
      icons.push(fallback.icon)
    }
    return icons
  }, [wagmiConnectors])

  const [renderedChain, setRenderedChain] = useState<ChainId | null>(null)
  const [isChainModalOpen, setIsChainModalOpen] = useState(false)
  const openChainContainer = (chain: ChainId) => {
    setRenderedChain(chain)
    setIsChainModalOpen(true)
  }
  const isPasskeyRecent = recentWallet?.record.chain === "webauthn"

  const handleRecentClick = (resolved: ResolvedLastWallet) => {
    switch (resolved.record.chain) {
      case "near":
        void signIn({ id: ChainType.Near, walletId: resolved.record.walletId })
        return
      case "evm": {
        const connector = wagmiConnectors.find(
          (c) => c.id === resolved.record.walletId
        )
        if (!connector) return
        void signIn({ id: ChainType.EVM, connector })
        return
      }
      case "solana":
        void signIn({
          id: ChainType.Solana,
          adapterName: resolved.record.walletId,
        })
        return
      case "stellar":
        void signIn({
          id: ChainType.Stellar,
          walletId: resolved.record.walletId,
        })
        return
      case "ton":
        void signIn({ id: ChainType.Ton })
        return
      case "tron":
        void signIn({ id: ChainType.Tron })
        return
      case "webauthn":
        webauthnUI.open()
        return
    }
  }

  const isSignedOutRestoring =
    bootstrapState === "signed_out" && restoreStatus === "restoring"
  const restoreTimedOut = useSignedOutRestoreTimeout(isSignedOutRestoring)

  const shouldShowRestoreSplash = shouldShowLoginRestoreSplash({
    bootstrapState,
    restoreStatus,
    interactionStatus,
    restoreTimedOut,
  })

  const loginReady = !shouldShowRestoreSplash

  return (
    <div
      data-testid="login-page"
      data-login-ready={loginReady ? "true" : "false"}
    >
      {shouldShowRestoreSplash ? (
        <SplashScreenContent />
      ) : (
        <div className="flex flex-1 flex-col items-center px-4 py-16 sm:py-20">
          <div className="flex w-full max-w-md flex-col items-start">
            <Link href="/" className="flex shrink-0 items-center gap-2">
              <span className="sr-only">Home</span>
              <NearIcon className="size-7" />
              <NearIntentsLogoIcon className="h-4 text-black dark:text-white" />
            </Link>

            <h1 className="mt-10! font-bold text-2xl text-gray-900 tracking-tight dark:text-white">
              Sign in or create an account
            </h1>

            <p className="mt-2! text-pretty font-medium text-gray-500 text-sm dark:text-gray-400">
              Use a passkey for the easiest experience, or connect a wallet.{" "}
              <button
                type="button"
                className="inline whitespace-nowrap font-medium text-gray-500 text-sm/6 underline hover:text-gray-900 dark:text-gray-400 dark:hover:text-white"
                onClick={() => {
                  openHelpScoutArticle(HelpScoutArticleKey.PASSKEY_VS_WALLET)
                }}
              >
                Wondering which to choose?
              </button>
            </p>

            {signInError !== null && (
              <ErrorMessage className="mt-4">{signInError}</ErrorMessage>
            )}

            <ReferredLoginNote />

            <button
              type="button"
              data-testid="login-passkey"
              onClick={() => {
                webauthnUI.open()
              }}
              className="-outline-offset-1 hover:-outline-offset-2 focus-visible:-outline-offset-2 relative mt-7 flex w-full flex-col gap-8 rounded-2xl bg-white p-4 text-left outline outline-gray-200 hover:outline-2 hover:outline-gray-400 focus-visible:outline-2 focus-visible:outline-gray-400 min-[350px]:p-5 dark:bg-gray-800 dark:outline-white/5"
            >
              <span className="flex items-start justify-between gap-2">
                <div className="relative flex size-10 items-center justify-center overflow-hidden rounded-full bg-gray-900 text-green-500 dark:bg-green-500/10 dark:text-green-500">
                  <TouchIdIcon className="size-6 shrink-0" />
                </div>
                {!recentWallet && (
                  <Badge variant="success" hideIcon>
                    Recommended
                  </Badge>
                )}
                {isPasskeyRecent && (
                  <Badge variant="success" hideIcon>
                    Recent
                  </Badge>
                )}
              </span>
              <span className="flex flex-col gap-1">
                <span className="font-semibold text-base/5 text-gray-900 dark:text-white">
                  Passkey for Touch ID and Face ID
                </span>
                <span className="text-balance font-medium text-gray-500 text-sm/5 dark:text-gray-400">
                  Use your device biometrics or passcode to sign in.
                </span>
              </span>
            </button>

            <div className="my-7 flex items-center gap-x-6 self-stretch">
              <div className="w-full flex-1 border-gray-200 border-t dark:border-white/10" />
              <span className="text-nowrap font-medium text-gray-500 text-sm/6 dark:text-gray-400">
                Web3 wallets
              </span>
              <div className="w-full flex-1 border-gray-200 border-t dark:border-white/10" />
            </div>

            <div className="grid w-full grid-cols-2 gap-2">
              {recentWallet && !isPasskeyRecent && (
                <WalletButton
                  name={recentWallet.name}
                  icon={recentWallet.icon}
                  onClick={() => handleRecentClick(recentWallet)}
                  dataTestId="login-recent"
                  badge={
                    <Badge variant="success" hideIcon>
                      Recent
                    </Badge>
                  }
                />
              )}
              <LoginButton
                name="NEAR Wallets"
                data-testid="login-near"
                icon={{
                  dark: "/static/icons/network/near-light.svg",
                  light: "/static/icons/network/near.svg",
                }}
                onClick={() => openChainContainer("near")}
              />
              <LoginButton
                name="EVM Wallets"
                data-testid="login-evm"
                icon={evmIcons}
                onClick={() => openChainContainer("evm")}
              />
              <LoginButton
                name="Solana Wallets"
                data-testid="login-solana"
                icon={{
                  dark: "/static/icons/network/solana-light.svg",
                  light: "/static/icons/network/solana.svg",
                }}
                onClick={() => openChainContainer("solana")}
              />
              <LoginButton
                name="Stellar Wallets"
                data-testid="login-stellar"
                icon={{
                  dark: "/static/icons/network/stellar-light.svg",
                  light: "/static/icons/network/stellar.svg",
                }}
                onClick={() => openChainContainer("stellar")}
              />
              <LoginButton
                name="TON Wallets"
                data-testid="login-ton"
                icon={{
                  dark: "/static/icons/network/ton.svg",
                  light: "/static/icons/network/ton.svg",
                }}
                onClick={() => signIn({ id: ChainType.Ton })}
              />
              <LoginButton
                name="Tron"
                data-testid="login-tron"
                icon={{
                  dark: "/static/icons/network/tron.svg",
                  light: "/static/icons/network/tron.svg",
                }}
                onClick={() => signIn({ id: ChainType.Tron })}
              />
            </div>

            <div className="mt-8 w-full border-gray-200 border-t pt-7 dark:border-white/10">
              <p className="text-balance text-left font-medium text-gray-500 text-sm dark:text-gray-400">
                By continuing, you agree to the{" "}
                <Link
                  href="/terms"
                  className="underline hover:text-gray-900 dark:hover:text-white"
                >
                  Terms of Service
                </Link>{" "}
                and{" "}
                <Link
                  href="/privacy"
                  className="underline hover:text-gray-900 dark:hover:text-white"
                >
                  Privacy Policy
                </Link>
                .
              </p>
            </div>
          </div>

          <ChainContainerModal
            chain={renderedChain}
            open={isChainModalOpen}
            onClose={() => setIsChainModalOpen(false)}
            onCloseAnimationEnd={() => setRenderedChain(null)}
            signIn={signIn}
          />
        </div>
      )}
    </div>
  )
}

function LoginButton({
  name,
  icon,
  onClick,
  "data-testid": dataTestId,
}: {
  name: string
  icon: ChainIcon | ChainIcon[]
  onClick: () => void
  "data-testid"?: string
}) {
  return (
    <WalletButton
      name={name}
      onClick={() => {
        onClick()
      }}
      dataTestId={dataTestId}
      icon={icon}
    />
  )
}
